<script setup lang="ts">
    import { computed } from 'vue'
    import { useClipboard } from '@vueuse/core'
    import PkCode from './PkCode.vue'
    import PkStreamingMarkdown from './PkStreamingMarkdown.vue'

    const { copy, copied } = useClipboard()

    const props = defineProps<{
        part: unknown
    }>()

    const toolPart = computed(() => {
        const part = props.part as {
            output?: {
                title: string
                content: string
                artifactType: string
                language?: string
            }
        }
        return part
    })

    const artifactTypeLabel = computed(() => {
        const labels: Record<string, string> = {
            code: 'Code',
            document: 'Document',
            markdown: 'Markdown',
            json: 'JSON',
            xml: 'XML',
            csv: 'CSV',
            html: 'HTML',
            yaml: 'YAML',
            sql: 'SQL',
            text: 'Text',
        }
        if (!toolPart.value.output?.artifactType) {
            return 'Artifact'
        }
        return labels[toolPart.value.output.artifactType] || 'Artifact'
    })

    const languageLabel = computed(() => {
        if (!toolPart.value.output?.language) {
            return ''
        }
        return ` (${toolPart.value.output.language})`
    })

    const isCodeType = computed(() => {
        return (
            toolPart.value.output?.artifactType === 'code' ||
            toolPart.value.output?.artifactType === 'json' ||
            toolPart.value.output?.artifactType === 'xml' ||
            toolPart.value.output?.artifactType === 'yaml' ||
            toolPart.value.output?.artifactType === 'html' ||
            toolPart.value.output?.artifactType === 'sql'
        )
    })

    const isMarkdownType = computed(() => {
        return toolPart.value.output?.artifactType === 'markdown'
    })

    const codeLanguage = computed(() => {
        if (toolPart.value.output?.language) {
            return toolPart.value.output.language
        }
        // Map artifact types to highlight.js languages
        const languageMap: Record<string, string> = {
            json: 'json',
            xml: 'xml',
            yaml: 'yaml',
            html: 'html',
            sql: 'sql',
        }
        if (!toolPart.value.output?.artifactType) {
            return 'plaintext'
        }
        return languageMap[toolPart.value.output.artifactType] || 'plaintext'
    })
</script>

<template>
    <div class="border border-surface-3 rounded-xl w-full overflow-hidden">
        <div
            class="px-sm py-6 font-bold bg-surface-1 text-12 border-b border-surface-3 text-word-3 flex justify-between items-center">
            <div class="flex flex-col items-start gap-4">
                <span>{{ toolPart?.output?.title }}</span>
                <span class="text-10 opacity-60 font-normal">
                    {{ artifactTypeLabel }}{{ languageLabel }}
                </span>
            </div>
            <VvButton
                :icon="copied ? 'ri:check-line' : 'ri:file-copy-line'"
                :label="copied ? $t('action.copied') : $t('action.copy')"
                modifiers="action-quiet-small"
                :title="$t('action.copy')"
                class="shrink-0"
                @click="copy(toolPart?.output?.content || '')" />
        </div>
        <div class="px-sm py-10">
            <PkCode
                v-if="isCodeType"
                :code="toolPart?.output?.content"
                :language="codeLanguage" />
            <PkStreamingMarkdown
                v-else-if="isMarkdownType"
                :markdown="toolPart?.output?.content"
                class="wysiwyg text-word-2" />
            <pre
                v-else
                class="text-word-2 whitespace-pre-wrap break-words"
                :class="{
                    italic:
                        toolPart?.output?.artifactType === 'document' ||
                        toolPart?.output?.artifactType === 'text',
                }"
                >{{ toolPart?.output?.content }}</pre
            >
        </div>
    </div>
</template>
